<template>
  <div :class="[menuClass]">
    <Menu v-slot="{ close }" as="div" class="relative inline-block text-left">
      <MenuButton
        :class="[
          'relative w-9 h-9 rounded-full flex items-center justify-center',
          menuBtnClass,
        ]"
      >
        <template v-if="isCompleted">
          <div
            class="w-full h-full bg-[#77b800] rounded-full flex items-center justify-center"
          >
            <FontAwesomeIcon
              :icon="['fas', 'medal']"
              class="text-white text-xl z-10"
            />
          </div>
        </template>

        <template v-else>
          <svg class="absolute inset-0 progress-ring" viewBox="0 0 36 36">
            <template v-for="(item, index) in items" :key="item.value">
              <path
                :d="sectorPath(index)"
                :fill="index < checkedCount ? '#7ed957' : '#393f47'"
                stroke="transparent"
                stroke-width="1"
              />
            </template>
          </svg>

          <span class="relative z-10 text-xs text-white">
            {{ checkedCount }}/{{ totalItems }}
          </span>
        </template>
      </MenuButton>

      <transition
        enter-active-class="transition ease-out duration-100"
        enter-from-class="transform opacity-0 scale-95"
        enter-to-class="transform opacity-100 scale-100"
        leave-active-class="transition ease-in duration-75"
        leave-from-class="transform opacity-100 scale-100"
        leave-to-class="transform opacity-0 scale-95"
      >
        <MenuItems
          :class="[
            'absolute right-0 z-10 w-80 origin-top-right rounded-lg bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none',
            dropdownClass,
          ]"
        >
          <div class="p-5">
            <div class="flex items-center justify-between mb-5">
              <h3 class="font-semibold text-xl">
                {{ $t('event.checklist.title') }}
              </h3>
              <button
                class="w-5 h-5 pr-1 flex items-center justify-center"
                @click="close"
              >
                <FontAwesomeIcon
                  :icon="['fal', 'xmark']"
                  class="text-black text-xl"
                />
              </button>
            </div>

            <ul class="space-y-3">
              <li
                v-for="item in items"
                :key="item.value"
                class="flex items-center justify-between text-sm"
              >
                <span class="text-gray-700">
                  <!-- eslint-disable vue/no-v-html -->
                  <span v-html="$t(item.label) + ' '"></span>
                  <a
                    v-if="item.route !== null"
                    style="text-decoration: underline"
                    :href="
                      useRoute(item.route, { event: usePage().props.event.id })
                    "
                  >
                    {{ $t(item.link_label) }}
                  </a>
                </span>
                <div
                  v-if="props.checked?.[item.value]"
                  class="w-6 h-6 rounded-full bg-[#77b800] flex items-center justify-center"
                >
                  <FontAwesomeIcon
                    :icon="['fas', 'check']"
                    class="text-[16px] text-white"
                  />
                </div>

                <div
                  v-else
                  class="w-6 h-6 rounded-full bg-[#8a5ed9] flex items-center justify-center"
                >
                  <FontAwesomeIcon
                    :icon="['fas', 'times']"
                    class="text-[16px] text-white"
                  />
                </div>
              </li>
            </ul>

            <div class="mt-5 flex items-center justify-end gap-3">
              <span class="text-xs text-gray-600">{{
                $t('event.checklist.need-help')
              }}</span>
              <a
                href="https://help.airlst.app/hc/de/sections/22264309794333-Los-geht-s-mit-der-AirLST-Suite"
                target="_blank"
                class="flex h-[30px] min-w-[70px] items-center justify-center gap-3 rounded-control bg-primary px-[20px] text-caption text-white outline-none hover:bg-[#8a52e6] transition-colors"
              >
                <span>{{ $t('event.checklist.visit-help-center') }}</span>
              </a>
            </div>
          </div>
        </MenuItems>
      </transition>
    </Menu>
  </div>
</template>

<script setup>
import { computed } from 'vue'
import { Menu, MenuButton, MenuItems } from '@headlessui/vue'
import { useRoute } from '@/composables/route'
import { usePage } from '@inertiajs/vue3'

const props = defineProps({
  items: {
    type: Array,
    required: true,
  },
  checked: {
    type: Object,
    required: true,
  },
  menuClass: {
    type: String,
    default: '',
  },
  menuBtnClass: {
    type: String,
    default: '',
  },
  dropdownClass: {
    type: String,
    default: '',
  },
})

const totalItems = computed(() => props.items.length)
const checkedCount = computed(
  () =>
    props.items.filter((item) => props.checked?.[item.value] === true).length,
)

const isCompleted = computed(() => checkedCount.value === totalItems.value)

function sectorPath(index) {
  const total = props.items.length
  const radius = 18
  const innerRadius = 11
  const center = 18
  const angle = (2 * Math.PI) / total
  const gap = 0.05
  const startAngle = angle * index + gap / 2
  const endAngle = startAngle + angle - gap

  const x1 = center + radius * Math.cos(startAngle)
  const y1 = center + radius * Math.sin(startAngle)
  const x2 = center + radius * Math.cos(endAngle)
  const y2 = center + radius * Math.sin(endAngle)

  const x3 = center + innerRadius * Math.cos(endAngle)
  const y3 = center + innerRadius * Math.sin(endAngle)
  const x4 = center + innerRadius * Math.cos(startAngle)
  const y4 = center + innerRadius * Math.sin(startAngle)

  const largeArcFlag = angle - gap > Math.PI ? 1 : 0

  return `M ${x1},${y1} A ${radius},${radius} 0 ${largeArcFlag},1 ${x2},${y2} L ${x3},${y3} A ${innerRadius},${innerRadius} 0 ${largeArcFlag},0 ${x4},${y4} Z`
}
</script>

<style scoped>
.progress-ring {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}
</style>
